import React, { useState } from 'react';
import { Search, Eye, CheckCircle, XCircle, RefreshCw, Trash2, Truck } from 'lucide-react';
import { format } from 'date-fns';

const STATUS_CONFIG = {
  pending: { label: 'En Attente', color: 'text-yellow-700 bg-yellow-100 border border-yellow-200' },
  confirmed: { label: 'Confirmée', color: 'text-green-700 bg-green-100 border border-green-200' },
  processing: { label: 'En Traitement', color: 'text-blue-700 bg-blue-100 border border-blue-200' },
  assigned: { label: 'Affectée', color: 'text-purple-700 bg-purple-100 border border-purple-200' },
  completed: { label: 'Terminée', color: 'text-gray-700 bg-gray-100 border border-gray-200' },
  cancelled: { label: 'Annulée', color: 'text-red-700 bg-red-100 border border-red-200' },
};

const SOURCE_LABELS = {
  whatsapp: { label: 'WhatsApp', icon: '💬', color: 'text-green-600 bg-green-50' },
  website: { label: 'Site Web', icon: '🌐', color: 'text-blue-600 bg-blue-50' },
  fatou_ia: { label: 'Fatou IA', icon: '🤖', color: 'text-purple-600 bg-purple-50' },
};

export default function ReservationCaptureTable({ reservations, filters, onFiltersChange, onUpdateStatus, onSelectReservation, onRefresh, onDelete }) {
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteClick = (e, id) => {
    e?.stopPropagation();
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async (id) => {
    setDeleteLoading(true);
    try {
      await onDelete?.(id);
      setDeleteConfirmId(null);
    } catch (err) {
      alert('Erreur lors de la suppression');
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try { return format(new Date(dateStr), 'dd/MM/yyyy HH:mm'); }
    catch { return '—'; }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Rechercher par nom, téléphone, email..."
              value={filters?.searchQuery}
              onChange={(e) => onFiltersChange({ ...filters, searchQuery: e?.target?.value })}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <select
            value={filters?.status || 'all'}
            onChange={(e) => onFiltersChange({ ...filters, status: e?.target?.value === 'all' ? null : e?.target?.value })}
            className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="pending">En Attente</option>
            <option value="confirmed">Confirmées</option>
            <option value="processing">En Traitement</option>
            <option value="assigned">Affectées</option>
            <option value="completed">Terminées</option>
            <option value="cancelled">Annulées</option>
          </select>
          <button
            onClick={onRefresh}
            className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <RefreshCw size={16} />
            <span className="hidden sm:inline">Actualiser</span>
          </button>
        </div>
      </div>
      {/* Count */}
      <div className="text-sm text-gray-500">
        {reservations?.length} réservation{reservations?.length !== 1 ? 's' : ''} trouvée{reservations?.length !== 1 ? 's' : ''}
      </div>
      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Collecte → Livraison</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Service</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Source</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Chauffeur</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {reservations?.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <Search size={32} />
                      <p className="text-sm">Aucune réservation trouvée</p>
                    </div>
                  </td>
                </tr>
              ) : (
                reservations?.map((reservation) => {
                  const statusCfg = STATUS_CONFIG?.[reservation?.status] || STATUS_CONFIG?.pending;
                  const sourceCfg = SOURCE_LABELS?.[reservation?.source] || SOURCE_LABELS?.fatou_ia;
                  return (
                    <tr key={reservation?.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(reservation?.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">{reservation?.customer_name}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div>{reservation?.customer_phone}</div>
                        {reservation?.customer_email && (
                          <div className="text-xs text-gray-400 truncate max-w-[140px]">{reservation?.customer_email}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="max-w-[160px]">
                          <div className="truncate text-xs">{reservation?.pickup_city || reservation?.pickup_address || '—'}</div>
                          <div className="text-xs text-gray-400">→ {reservation?.delivery_city || '—'}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {reservation?.service_type || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sourceCfg?.color}`}>
                          <span>{sourceCfg?.icon}</span>
                          <span className="hidden sm:inline">{sourceCfg?.label}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusCfg?.color}`}>
                          {statusCfg?.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {reservation?.assigned_driver_name ? (
                          <span className="flex items-center gap-1 text-purple-700 text-xs font-medium">
                            <Truck size={12} />
                            {reservation?.assigned_driver_name}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Non affecté</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {deleteConfirmId === reservation?.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDeleteConfirm(reservation?.id)}
                              disabled={deleteLoading}
                              className="px-2 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                            >
                              {deleteLoading ? <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle size={12} />}
                              Oui
                            </button>
                            <button
                              onClick={() => setDeleteConfirmId(null)}
                              className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300"
                            >
                              Non
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => onSelectReservation?.(reservation)}
                              className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                              title="Voir les détails"
                            >
                              <Eye size={16} />
                            </button>
                            {reservation?.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => onUpdateStatus?.(reservation?.id, 'confirmed', null)}
                                  className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                  title="Confirmer"
                                >
                                  <CheckCircle size={16} />
                                </button>
                                <button
                                  onClick={() => onUpdateStatus?.(reservation?.id, 'cancelled', 'Annulée par admin')}
                                  className="p-1.5 text-orange-500 hover:bg-orange-100 rounded-lg transition-colors"
                                  title="Annuler"
                                >
                                  <XCircle size={16} />
                                </button>
                              </>
                            )}
                            <button
                              onClick={(e) => handleDeleteClick(e, reservation?.id)}
                              className="p-1.5 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}